import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "../../lib/utils"

/**
 * Button component variants using class-variance-authority
 * Defines different visual styles and sizes for buttons
 * Uses shadcn/ui Orange theme primary color
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Primary: Uses theme primary color with white hover effect
        default: "bg-primary text-primary-foreground hover:bg-white hover:text-primary shadow-md transition-colors duration-200",
        
        // Destructive: For delete/dangerous actions
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md",
        
        // Outline: Transparent with primary border
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-sm",
        
        // Secondary: Light background
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm",
        
        // Ghost: No background, minimal styling
        ghost: "hover:bg-accent hover:text-accent-foreground",
        
        // Link: Text-only styling
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
)

/**
 * Modern Button component with shadcn/ui Orange theme styling
 * Features:
 * - Multiple variants (default, outline, ghost, etc.)
 * - Uses shadcn/ui Orange theme primary color
 * - Accessibility features (focus states, disabled states)
 * - Customizable via className prop
 */
const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? "span" : "button"
  
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }